import './bootstrapThemeBeforePaint';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import App from './App.tsx';
import SharedVideoPage from './components/SharedVideoPage.tsx';
import { PreferencesProvider } from './context/PreferencesContext';
import './index.css';
import i18n from './lib/i18n';
import { getAcvParentOriginForPostMessage } from './utils/acvParentOrigin';
import { isValidAcvParentMessageOrigin } from './utils/acvMessageOrigin';
import { applyLanguageFromAcv } from './utils/acvLanguage';
import { applyIdentity, normalizeSlug, purgeLegacyAnonIdentity } from './lib/identity';
import { AppToaster } from './lib/toast';
import {
  persistAiRecordingSidebarState,
  applyAiRecordingSidebarWidth,
} from './lib/sidebar-state';
const MODULE_ID = 'aiRecording';

function applyAiRecordingDeeplink(path: string): void {
  if (!path || typeof path !== 'string') return;
  const normalized = path.startsWith('/') ? path : `/${path}`;
  const current = window.location.pathname + window.location.search + window.location.hash;
  if (current !== normalized) {
    window.history.pushState(null, '', normalized);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }
}

function flushPendingAiRecordingDeeplinkFromStorage(): void {
  try {
    const pending = sessionStorage.getItem('airecording:pendingNavigate');
    if (!pending) return;
    sessionStorage.removeItem('airecording:pendingNavigate');
    applyAiRecordingDeeplink(pending);
  } catch {
    // ignore
  }
}

function requestInitialThemeFromParent(): void {
  try {
    if (window.parent === window) return;
    const targetOrigin = getAcvParentOriginForPostMessage();
    if (!targetOrigin) return;

    window.parent.postMessage(
      { type: 'acv-request-initial-theme', moduleId: MODULE_ID },
      targetOrigin,
    );
  } catch {
    // ignore
  }
}

/**
 * Listener global de postMessage — registrado ANTES de createRoot (patrón Communities main.tsx §4.1).
 *
 * Maneja en un solo bloque:
 *  1. acv-toggle-left-sidebar → re-emite como CustomEvent para que el Sidebar lo capture
 *     sin race conditions (igual que Communities: main.tsx → 'acv-toggle-left-sidebar-from-host').
 *  2. acv-expand-left-sidebar → para que ACV pueda forzar expansión al navegar a este módulo.
 *  3. acv.airecording.command (voz) → CustomEvent 'acv-voice-command'.
 *  4. acv-layout-info → setea --acv-embed-top-offset / --acv-navbar-height.
 */
window.addEventListener('message', (event: MessageEvent) => {
  try {
    const data: any = (event as any)?.data;
    if (!data || typeof data !== 'object') return;

    // 1. Toggle sidebar desde ACV navbar
    if (data.type === 'acv-toggle-left-sidebar') {
      window.dispatchEvent(new CustomEvent('acv-toggle-left-sidebar-from-host'));
      return;
    }

    // 2. Forzar expansión (al entrar al módulo desde ACV)
    if (data.type === 'acv-expand-left-sidebar') {
      window.dispatchEvent(new CustomEvent('acv-expand-left-sidebar-from-host'));
      return;
    }

    // 2b. Deep-link from ACV notification bell (e.g. /share/:token#comments)
    if (data.type === 'acv-airecording-deeplink' && typeof data.path === 'string') {
      applyAiRecordingDeeplink(data.path);
      return;
    }

    // 3. Comandos de voz desde ACV
    if (data.type === 'acv.airecording.command' && data.action === 'voiceCommand') {
      const payload = data.payload || {};
      const parsed = payload.parsed;
      const raw =
        payload.raw ??
        (parsed && typeof parsed === 'object' && typeof (parsed as { originalCommand?: string }).originalCommand === 'string'
          ? (parsed as { originalCommand: string }).originalCommand
          : typeof payload.parsed === 'string'
            ? payload.parsed
            : '');
      window.dispatchEvent(new CustomEvent('acv-voice-command', { detail: { parsed, raw } }));
      const correlationId =
        data.correlationId ||
        data?.meta?.correlationId ||
        data?.payload?.correlationId ||
        null;
      if (correlationId && event.source && typeof (event.source as Window).postMessage === 'function') {
        try {
          (event.source as Window).postMessage(
            {
              type: 'acv.voice.ack',
              action: 'voiceCommandAck',
              module: 'aiRecording',
              correlationId,
              ack: true,
              status: 'ack',
              ts: Date.now(),
            },
            event.origin || '*'
          );
        } catch (e) {
          console.warn('[aiRecording] Voice ACK postMessage failed:', e);
        }
      }
      return;
    }

    // Language from ACV host (iframe) — same contract as Communities → LearnHub
    if (data.type === 'acv-language-change' && data.language != null) {
      if (!isValidAcvParentMessageOrigin(event.origin)) return;
      applyLanguageFromAcv(i18n, data.language);
      return;
    }
    if (data.type === 'acv-session-update' && data.language != null) {
      if (!isValidAcvParentMessageOrigin(event.origin)) return;
      applyLanguageFromAcv(i18n, data.language);
      return;
    }

    // 4. Layout info: offset del navbar ACV + SET del left sidebar (no toggle).
    if (data.type === 'acv-layout-info') {
      const navbarHeight = Math.max(0, Number(data.navbarHeight) || 0);
      const verticalNavbarWidth = data.verticalNavbarWidth || '0px';
      document.documentElement.style.setProperty('--acv-embed-top-offset', `${navbarHeight}px`);
      document.documentElement.style.setProperty('--acv-navbar-height', `${navbarHeight}px`);
      document.documentElement.style.setProperty('--acv-vertical-navbar-width', verticalNavbarWidth);
      if (
        typeof data.leftSidebarCollapsed === 'boolean' &&
        isValidAcvParentMessageOrigin(event.origin)
      ) {
        persistAiRecordingSidebarState(data.leftSidebarCollapsed);
        applyAiRecordingSidebarWidth(data.leftSidebarCollapsed);
        window.dispatchEvent(
          new CustomEvent('acv-set-left-sidebar-state', {
            detail: { collapsed: data.leftSidebarCollapsed },
          }),
        );
      }
      window.dispatchEvent(new CustomEvent('acv-layout-info-applied', { detail: data }));
      return;
    }

    // 5. Comandos de tema (captura temprana para evitar pérdida por race con React)
    if (
      data.type === 'theme-change' ||
      data.type === 'theme-reload' ||
      data.type === 'APPLY_THEME'
    ) {
      (window as any).__acvPendingThemeCommand = data;
      window.dispatchEvent(new CustomEvent('extension-theme-command', { detail: data }));
    }

    // 6. Payload de sesión del usuario enviado por ACV App al cargar el iframe.
    // Contiene accessToken, SASToken, email, slug, role — necesarios para acceder
    // a recursos privados de Azure Blob (logos, archivos) con firma SAS válida.
    const msgUser = data.message as {
      user?: { slug?: string; accessToken?: string; email?: string; role?: string; SASToken?: string };
      isFromACV?: boolean;
    } | undefined;
    if (msgUser?.user && msgUser.isFromACV && isValidAcvParentMessageOrigin(event.origin)) {
      const u = msgUser.user;
      // Goes first: if this is a different user, the previous user's cache is cleared.
      if (u.email) applyIdentity(u.email, u.slug);
      else if (normalizeSlug(u.slug)) localStorage.setItem('slug', normalizeSlug(u.slug) as string);
      if (u.accessToken) localStorage.setItem('access_token', u.accessToken);
      if (u.role) localStorage.setItem('user-role', u.role.toLowerCase());
      if (u.SASToken) {
        localStorage.setItem('SASToken', u.SASToken);
        // Notificar al hook useSchoolLogo para que recargue el logo con el token fresco
        window.dispatchEvent(new CustomEvent('airecording-refresh-school-logo'));
      }
      try {
        const prev = JSON.parse(localStorage.getItem('msg') || '{}') as { user?: Record<string, unknown> };
        localStorage.setItem('msg', JSON.stringify({ ...prev, user: { ...(prev.user || {}), ...u } }));
      } catch {
        localStorage.setItem('msg', JSON.stringify({ user: u }));
      }
    }

    // Phase 13-J: channel scope is fetched directly from ACV-Backend by ChannelDirectory
    // on startup — no need to relay scopeJson through postMessage.
  } catch {
    // ignore
  }
});

window.addEventListener('acv-airecording-deeplink', (event: Event) => {
  const detail = (event as CustomEvent<{ path?: string }>).detail;
  if (detail?.path) {
    applyAiRecordingDeeplink(detail.path);
  }
});

purgeLegacyAnonIdentity();
flushPendingAiRecordingDeeplinkFromStorage();

// Solicitar tema inicial al parent para cubrir primer ingreso (race-safe).
requestInitialThemeFromParent();
setTimeout(() => requestInitialThemeFromParent(), 300);
setTimeout(() => requestInitialThemeFromParent(), 900);

// Heartbeat (acv-leftsidebar-metrics) is sent from StandardLeftSidebar / AdminCenterLeftSidebarClone
// after React mounts — same pattern as AdminCenter. An early heartbeat here hid ACV's
// ExternalAppPlaceholder while auth and the app shell were still loading.

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <PreferencesProvider>
      <Router>
        <Routes>
          <Route path="/share/:shareToken" element={<SharedVideoPageWrapper />} />
          <Route path="/*" element={<App />} />
        </Routes>
      </Router>
      <AppToaster />
    </PreferencesProvider>
  </StrictMode>
);

function SharedVideoPageWrapper() {

  const shareToken = window.location.pathname.split('/share/')[1];
  return <SharedVideoPage shareToken={shareToken} />;
}